import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import BrandFooter from './BrandFooter';
import * as FiIcons from 'react-icons/fi';

const {FiShield,FiDollarSign,FiClock,FiCheckCircle,FiArrowRight,FiInfo,FiStar,FiUsers,FiHeart,FiCalendar,FiAlertTriangle,FiPhone,FiPlay}=FiIcons;

const ResultsPage=({responses})=> {
  const navigate=useNavigate();
  const [timeLeft,setTimeLeft]=useState(15 * 60);// 15 minutes in seconds 

  // Countdown timer for urgency 
  useEffect(()=> {
    const timer=setInterval(()=> {
      setTimeLeft(prev=> prev > 0 ? prev - 1 : 0);
    },1000);
    return ()=> clearInterval(timer);
  },[]);

  const formatTime=(seconds)=> {
    const mins=Math.floor(seconds / 60);
    const secs=seconds % 60;
    return `${mins}:${secs.toString().padStart(2,'0')}`;
  };

  // Enhanced recommendations based on responses 
  const getRecommendations=()=> {
    const age=responses.age;
    const familyStatus=responses.familyStatus;
    const timeline=responses.timeline;
    const goals=responses.goals || [];
    const income=responses.income;
    const debts=responses.debts || [];

    let primaryRecommendation={};

    // Determine coverage amount based on income and debts 
    let coverageMultiplier=10;// Default 10x income 
    if (debts.includes('mortgage')) coverageMultiplier +=2;
    if (familyStatus==='young-kids') coverageMultiplier +=3;
    if (goals.includes('education-funding')) coverageMultiplier +=2;

    // Calculate recommended coverage 
    let recommendedCoverage='$500,000';
    let monthlyRate='$35';

    if (income==='under-50k') {
      recommendedCoverage='$250,000 - $500,000';
      monthlyRate='$25 - $45';
    } else if (income==='50k-100k') {
      recommendedCoverage='$500,000 - $750,000';
      monthlyRate='$35 - $65';
    } else if (income==='100k-200k') {
      recommendedCoverage='$750,000 - $1,500,000';
      monthlyRate='$55 - $125';
    } else if (income==='200k-500k') {
      recommendedCoverage='$1,000,000 - $2,500,000';
      monthlyRate='$85 - $225';
    } else if (income==='over-500k') {
      recommendedCoverage='$2,500,000+';
      monthlyRate='$185+';
    } 

    // Determine term length 
    let termLength='20';
    if (timeline==='10-years') termLength='10';
    if (timeline==='30-years') termLength='30';

    primaryRecommendation={
      type: `${termLength}-Year Term Life Insurance`,
      coverage: recommendedCoverage,
      monthlyRate: monthlyRate,
      description: 'Maximum protection at the most affordable rates. Perfect for growing families and income replacement.',
      whyRecommended: getWhyRecommended(familyStatus,goals,income),
      benefits: [ 
        'Guaranteed level premiums',
        'No medical exam required',
        'Instant approval available',
        'Convertible to permanent coverage',
        'Cancel anytime without penalty' 
      ]
    };

    return primaryRecommendation;
  };

  const getWhyRecommended=(familyStatus,goals,income)=> {
    if (familyStatus==='young-kids') {
      return "With young children depending on you,term life insurance provides maximum coverage during their most vulnerable years at rates you can afford.";
    } 
    if (goals.includes('income-replacement')) {
      return "Since income replacement is your priority,this coverage ensures your family can maintain their lifestyle without financial stress.";
    } 
    if (income==='over-500k' || income==='200k-500k') {
      return "Given your income level,this coverage protects the lifestyle and opportunities you've built for your family.";
    } 
    return "Based on your situation,this coverage provides the right balance of protection and affordability.";
  };

  const testimonials=[ 
    {
      name: "Sarah M.",
      profile: "Mother of 2,Age 34",
      quote: "I put off getting life insurance for years because I didn't know what I needed. This questionnaire made it so clear,and I got approved instantly!",
      coverage: "$750,000 Term Life",
      rating: 5
    },
    {
      name: "Mike R.",
      profile: "Business Owner,Age 42",
      quote: "As a business owner with employees depending on me,I needed substantial coverage fast. The whole process took less than 10 minutes.",
      coverage: "$1.5M Term Life",
      rating: 5
    },
    {
      name: "Jennifer L.",
      profile: "Single Mom,Age 29",
      quote: "Being a single mom,I couldn't afford to wait any longer. The rates were better than I expected and no medical exam was needed.",
      coverage: "$500,000 Term Life",
      rating: 5
    },
    {
      name: "David K.",
      profile: "Recent Graduate,Age 26",
      quote: "Just started my career and wanted to lock in low rates while I'm young and healthy. So glad I didn't wait!",
      coverage: "$300,000 Term Life",
      rating: 5
    } 
  ];

  const urgencyFactors=[ 
    "Rates increase with age - lock in today's low rates",
    "Health can change unexpectedly - apply while you're healthy",
    "Your family needs protection starting immediately",
    "Special promotional rates expire soon" 
  ];

  const recommendation=getRecommendations();

  // Updated function to redirect to Burbank Insurance Services 
  const handleGetCoverage=()=> {
    // Direct redirect to Burbank Insurance Services quote page 
    window.open('https://quote.burbankinsuranceservices.com/JOT21P','_blank');
  };

  return ( 
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8"> 
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"> 

        {/* Urgency Timer */} 
        <motion.div
          initial={{opacity: 0,y: -20}}
          animate={{opacity: 1,y: 0}}
          className="bg-red-600 text-white text-center py-3 px-6 rounded-xl mb-8 shadow-lg"
        > 
          <div className="flex items-center justify-center space-x-4"> 
            <SafeIcon icon={FiClock} className="w-5 h-5" /> 
            <span className="font-semibold"> 
              Limited Time: Lock in today's rates before they increase 
            </span> 
            <div className="bg-white text-red-600 px-3 py-1 rounded font-bold"> 
              {formatTime(timeLeft)} 
            </div> 
          </div> 
        </motion.div> 

        {/* Header */} 
        <motion.div
          initial={{opacity: 0,y: 30}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.8}}
          className="text-center mb-12"
        > 
          <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-6"> 
            <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-white" /> 
          </div> 
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"> 
            Perfect! Here's Your Personalized Life Insurance Plan 
          </h1> 
          <p className="text-xl text-gray-600 max-w-3xl mx-auto"> 
            Based on your responses,we've identified the ideal coverage to protect your family's future. 
          </p> 
        </motion.div> 

        {/* Primary Recommendation Card */} 
        <motion.div
          initial={{opacity: 0,y: 30}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.2,duration: 0.8}}
          className="bg-gradient-to-r from-primary-700 to-primary-800 rounded-2xl p-8 text-white mb-8 relative overflow-hidden"
        > 
          <div className="absolute top-0 right-0 bg-accent-500 text-white px-6 py-2 rounded-bl-2xl font-bold text-sm"> 
            RECOMMENDED FOR YOU 
          </div> 
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4"> 
            <div> 
              <div className="flex items-center mb-4"> 
                <SafeIcon icon={FiShield} className="w-8 h-8 mr-3" /> 
                <h2 className="text-3xl font-bold">{recommendation.type}</h2> 
              </div> 
              <p className="text-blue-100 text-lg mb-6">{recommendation.description}</p> 
              <div className="bg-white/10 rounded-xl p-4 mb-6"> 
                <p className="text-blue-100 text-sm font-medium mb-2">Why this is perfect for you:</p> 
                <p className="text-white">{recommendation.whyRecommended}</p> 
              </div> 
            </div> 
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6"> 
              <div className="text-center mb-6"> 
                <div className="text-blue-200 text-sm mb-2">Recommended Coverage</div> 
                <div className="text-3xl font-bold mb-4">{recommendation.coverage}</div> 
                <div className="text-blue-200 text-sm mb-2">Starting at just</div> 
                <div className="text-4xl font-bold text-accent-400 mb-6">{recommendation.monthlyRate}/month</div> 
              </div> 
              
              <button
                onClick={handleGetCoverage}
                className="w-full bg-accent-500 hover:bg-accent-600 text-white font-bold py-4 px-6 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center mb-4"
              > 
                Get This Coverage Now 
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" /> 
              </button> 
              
              <div className="text-center"> 
                <p className="text-blue-200 text-sm mb-2">✓ No Medical Exam ✓ Instant Approval ✓ Cancel Anytime</p> 
              </div> 
            </div> 
          </div> 
        </motion.div> 

        {/* Educational Video Section - Before proceeding */}
        <motion.div
          initial={{opacity: 0,y: 30}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.3,duration: 0.8}}
          className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-8 mb-8 border-2 border-yellow-200"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiPlay} className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Want to Learn More Before You Decide?
            </h3>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
              Still not sure about the different types of life insurance? Watch this educational video to understand your options better before proceeding with your application.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-4">
              <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/qIo_x5B9Kdk"
                  title="Life Insurance Education Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Perfect if you're unsure:</strong> Learn the differences between term life, whole life, and universal life insurance.
                </p>
                <div className="flex flex-wrap justify-center gap-2 text-xs">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">✓ Term vs Permanent</span>
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded">✓ Cost Comparison</span>
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">✓ When to Buy</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-6">
            <p className="text-gray-600 text-sm mb-4">
              After watching, you'll feel more confident about your decision to proceed with the recommended coverage above.
            </p>
            <button
              onClick={handleGetCoverage}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg flex items-center mx-auto"
            >
              I'm Ready - Get My Coverage
              <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Why Ethos Section */} 
        <motion.div
          initial={{opacity: 0,y: 30}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.4,duration: 0.8}}
          className="bg-white rounded-2xl p-8 shadow-lg mb-8"
        > 
          <div className="text-center mb-8"> 
            <h3 className="text-3xl font-bold text-gray-900 mb-4"> 
              Get Quality Insurance Fast with Ethos 
            </h3> 
            <p className="text-xl text-gray-600 max-w-3xl mx-auto"> 
              Skip the delays,paperwork,and medical exams. Get covered from the comfort of your home in minutes,not months. 
            </p> 
          </div> 

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"> 
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl"> 
              <SafeIcon icon={FiClock} className="w-12 h-12 text-primary-600 mx-auto mb-4" /> 
              <h4 className="font-bold text-gray-900 mb-2">5-Minute Application</h4> 
              <p className="text-gray-600 text-sm">Simple online process,no lengthy forms or appointments</p> 
            </div> 
            <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl"> 
              <SafeIcon icon={FiShield} className="w-12 h-12 text-accent-600 mx-auto mb-4" /> 
              <h4 className="font-bold text-gray-900 mb-2">No Medical Exam</h4> 
              <p className="text-gray-600 text-sm">Just answer health questions online,no needles or doctors</p> 
            </div> 
            <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl"> 
              <SafeIcon icon={FiCheckCircle} className="w-12 h-12 text-purple-600 mx-auto mb-4" /> 
              <h4 className="font-bold text-gray-900 mb-2">Instant Approval</h4> 
              <p className="text-gray-600 text-sm">Get approved and covered immediately,protection starts today</p> 
            </div> 
          </div> 

          {/* Trust Logos */} 
          <div className="text-center mb-8"> 
            <p className="text-sm font-medium text-gray-500 mb-6 uppercase tracking-wide"> 
              Trusted by Leading Publications 
            </p> 
            <div className="flex justify-center items-center space-x-8 opacity-60"> 
              <img src="https://app1.sharemyimage.com/2025/08/18/cdnlogo.com_business-insider.png" alt="Business Insider" className="h-8 object-contain" /> 
              <img src="https://app1.sharemyimage.com/2025/08/18/png-clipart-logo-forbes-industry-magazine-black-cover-company-text.png" alt="Forbes" className="h-8 object-contain" /> 
              <img src="https://app1.sharemyimage.com/2025/08/18/FORTUNE-LOGO-2016.png" alt="Fortune" className="h-8 object-contain" /> 
            </div> 
          </div> 

          {/* Ethos Stats */} 
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6"> 
            <div className="text-center"> 
              <div className="text-3xl font-bold text-primary-600 mb-2">$2B+</div> 
              <p className="text-gray-600">Coverage Delivered</p> 
            </div> 
            <div className="text-center"> 
              <div className="text-3xl font-bold text-accent-600 mb-2">100K+</div> 
              <p className="text-gray-600">Families Protected</p> 
            </div> 
            <div className="text-center"> 
              <div className="text-3xl font-bold text-purple-600 mb-2">A+ Rating</div> 
              <p className="text-gray-600">Financial Strength</p> 
            </div> 
          </div> 
        </motion.div> 

        {/* Customer Testimonials */} 
        <motion.div
          initial={{opacity: 0,y: 30}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.6,duration: 0.8}}
          className="mb-8"
        > 
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-8"> 
            Real Stories from Real Customers 
          </h3> 
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> 
            {testimonials.map((testimonial,index)=> ( 
              <motion.div
                key={index}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{delay: 0.7 + index * 0.1}}
                className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-primary-500"
              > 
                <div className="flex items-center mb-4"> 
                  {[...Array(testimonial.rating)].map((_,i)=> ( 
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-accent-500 fill-current" /> 
                  ))} 
                </div> 
                <p className="text-gray-700 mb-4 italic">"{testimonial.quote}"</p> 
                <div className="flex justify-between items-center"> 
                  <div> 
                    <p className="font-semibold text-gray-900">{testimonial.name}</p> 
                    <p className="text-sm text-gray-600">{testimonial.profile}</p> 
                  </div> 
                  <div className="text-right"> 
                    <p className="text-sm text-primary-600 font-medium">{testimonial.coverage}</p> 
                  </div> 
                </div> 
              </motion.div> 
            ))} 
          </div> 
        </motion.div> 

        {/* Urgency Section */} 
        <motion.div
          initial={{opacity: 0,y: 30}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.8,duration: 0.8}}
          className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 text-white mb-8"
        > 
          <div className="text-center mb-8"> 
            <SafeIcon icon={FiAlertTriangle} className="w-16 h-16 text-accent-400 mx-auto mb-4" /> 
            <h3 className="text-3xl font-bold mb-4">Don't Wait Any Longer</h3> 
            <p className="text-xl text-red-100 max-w-2xl mx-auto"> 
              Every day you delay puts your family at risk. Here's why you need to act today: 
            </p> 
          </div> 

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"> 
            {urgencyFactors.map((factor,index)=> ( 
              <div key={index} className="flex items-start"> 
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-accent-400 mr-3 mt-1 flex-shrink-0" /> 
                <span className="text-red-100">{factor}</span> 
              </div> 
            ))} 
          </div> 

          <div className="text-center"> 
            <button
              onClick={handleGetCoverage}
              className="bg-accent-500 hover:bg-accent-600 text-white font-bold py-4 px-8 rounded-xl text-xl transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center mx-auto mb-4"
            > 
              Protect My Family Now 
              <SafeIcon icon={FiArrowRight} className="ml-2 w-6 h-6" /> 
            </button> 
            <p className="text-red-200 text-sm"> 
              ⚡ Instant approval available • No medical exam required • Coverage starts immediately 
            </p> 
          </div> 
        </motion.div> 

        {/* Final CTA with Benefits */} 
        <motion.div
          initial={{opacity: 0,y: 30}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 1.0,duration: 0.8}}
          className="bg-white rounded-2xl p-8 shadow-lg text-center"
        > 
          <h3 className="text-3xl font-bold text-gray-900 mb-6"> 
            Ready to Secure Your Family's Future? 
          </h3> 
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8 text-sm"> 
            <div className="flex items-center justify-center p-3 bg-orange-50 rounded-lg"> 
              <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-accent-600 mr-2" /> 
              <span className="text-accent-800">No Medical Exam</span> 
            </div> 
            <div className="flex items-center justify-center p-3 bg-blue-50 rounded-lg"> 
              <SafeIcon icon={FiClock} className="w-5 h-5 text-primary-600 mr-2" /> 
              <span className="text-primary-800">5-Min Application</span> 
            </div> 
            <div className="flex items-center justify-center p-3 bg-purple-50 rounded-lg"> 
              <SafeIcon icon={FiShield} className="w-5 h-5 text-purple-600 mr-2" /> 
              <span className="text-purple-800">Instant Approval</span> 
            </div> 
            <div className="flex items-center justify-center p-3 bg-yellow-50 rounded-lg"> 
              <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-yellow-600 mr-2" /> 
              <span className="text-yellow-800">Best Rates</span> 
            </div> 
            <div className="flex items-center justify-center p-3 bg-red-50 rounded-lg"> 
              <SafeIcon icon={FiHeart} className="w-5 h-5 text-red-600 mr-2" /> 
              <span className="text-red-800">Family Protected</span> 
            </div> 
          </div> 

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center"> 
            <button
              onClick={handleGetCoverage}
              className="bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-700 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 shadow-xl flex items-center"
            > 
              Start My Application 
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" /> 
            </button> 
            
            <div className="text-center"> 
              <p className="text-gray-600 text-sm mb-2">Questions? Speak with a licensed agent</p> 
              <a 
                href="tel:+18005551234" 
                className="flex items-center justify-center text-primary-600 hover:text-primary-700 font-medium"
              > 
                <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2" /> 
                (800) 555-1234 
              </a> 
            </div> 
          </div> 

          {/* Brand Footer */} 
          <div className="mt-8 pt-8 border-t border-gray-200"> 
            <BrandFooter variant="light" /> 
          </div> 
        </motion.div> 
      </div> 
    </div> 
  );
};

export default ResultsPage;